import { mutate } from 'swr';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { apiUrl } from '@dashboard/services/env';
import logException from '@dashboard/services/logException';
import request, { fetcher } from '@dashboard/services/request';

export const updateProduct = async (values, productId, sellerId, token) => {
  try {
    const id = productId || (values && values._id);
    const key = id ? `/sellers/${sellerId}/products/${id}` : `/sellers/${sellerId}/products`;

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    // mutate existing product or add new
    mutate(`/products/${id ? id : response.data._id}`, response.data);

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save product', data: null };
  }
};

async function createStripePrice(productId, sellerId, token) {
  try {
    const response = await request(
      apiUrl + `/sellers/${sellerId}/products/${productId}/create-stripe-price`,
      token,
      {
        method: 'POST',
      },
    );

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    mutate(`/products/${productId}`, response.data);

    return { data: response.data, error: null };
  } catch (error) {
    logException({ message: 'Failed to create stripe price', error });
    if (error && error.message) {
      return { error: error.message };
    }

    return {
      error: 'Unable to create subscription plan in Stripe',
      data: null,
    };
  }
}

export const saveVariant = async (values, variantId, productId, sellerId, token) => {
  try {
    const key = variantId
      ? `/sellers/${sellerId}/products/${productId}/variants/${variantId}`
      : `/sellers/${sellerId}/products/${productId}/variants`;

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save variant', data: null };
  }
};

export const updateProductMutation = async (values, productId, sellerId, token) => {
  const id = productId || (values && values._id);
  const key = id ? `/sellers/${sellerId}/products/${id}` : `/sellers/${sellerId}/products`;

  return fetcher(key, token, {
    method: 'POST',
    body: JSON.stringify(values),
  });
};

export default function useProductQueries() {
  const { token, sellerId } = useAuth();

  return {
    updateProduct: (values, productId) => updateProduct(values, productId, sellerId, token),
    updateProductMutation: (values, productId) =>
      updateProductMutation(values, productId, sellerId, token),
    createStripePrice: productId => createStripePrice(productId, sellerId, token),
    saveVariant: (values, variantId, productId) =>
      saveVariant(values, variantId, productId, sellerId, token),
  };
}
